<script lang='ts' setup>
import { ref } from 'vue'
import RoomieChipSelector from '@lahaus-roomie/roomie/src/components/RoomieChipSelector/index.vue'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'

import type { Props } from './types'

import AppSlider from '@/components/AppSlider/index.vue'
import './_index.scss'

const props = withDefaults(defineProps<Props>(), {
  isScheduleCtaDisabled: false
})

const emit = defineEmits(['fullTurnSelected'])
const { t } = useI18n()

const selectedDayIdx = ref<number>(0)
const selectedHourIdx = ref<number>(0)

const handleDaySelect = (idx: number) => {
  selectedDayIdx.value = idx
}

const handleHourSelect = (idx: number) => {
  selectedHourIdx.value = idx
}

const handleScheduleButton = () => {
  const selectedDay = `${props.turns[selectedDayIdx.value].label_number_day} de ${props.turns[selectedDayIdx.value].label_month}`.toLocaleLowerCase()
  const selectedHour = props.turns[selectedDayIdx.value].schedules[selectedHourIdx.value!].label_turn_hour.toLocaleLowerCase()
  const fullStr = `El ${selectedDay} a las ${selectedHour}`
  emit('fullTurnSelected', { text: fullStr })
}

</script>

<script lang='ts'>
export default {
  name: 'AppChatScheduleTurnSelection'
}
</script>

<template>
  <section class="schedule-turn-selection schedule-turn-selection__container">
    <p
      v-if="helperText"
      id="app-chat-schedule-turn-selection-helper-text"
      class="!mb-0 body-2-m text-pine-600">
      {{ helperText }}
    </p>

    <section>
      <p class="body-2-sb text-pine-600">
        {{ t('dateSelection.title') }}
      </p>

      <AppSlider
        id="app-chat-schedule-turn-selection-day-chip-selector-slider"
        lateral-paddings="hidden"
        :slider-scroll="400"
        show-buttons>
        <RoomieChipSelector
          v-for="day, idx in turns"
          :id="`app-chat-schedule-turn-selection-day-chip-selector-${idx}`"
          :key="`app-chat-schedule-turn-selection-day-chip-selector-${idx}`"
          data-lh-id="app-chat-schedule-turn-selection-day-chip-selector"
          class="app-chat-schedule-turn-selection__day-chip-selector"
          size="sm"
          :is-selected="selectedDayIdx === idx"
          @click="() => handleDaySelect(idx)">
          <template #content>
            <p class="text-14 pointer-events-none whitespace-nowrap">
              {{ day.label_number_day }} {{ day.label_month }}
            </p>
          </template>
        </RoomieChipSelector>
      </AppSlider>
    </section>

    <section>
      <p class="body-2-sb text-pine-600">
        {{ t('timeSelection.title') }}
      </p>

      <AppSlider
        id="app-chat-schedule-turn-selection-day-chip-selector-slider"
        lateral-paddings="hidden"
        :slider-scroll="100"
        :show-buttons="true">
        <RoomieChipSelector
          v-for="hour, idx in turns[selectedDayIdx].schedules"
          :id="`app-chat-schedule-turn-selection-hour-chip-selector-${idx}`"
          :key="`app-chat-schedule-turn-selection-hour-chip-selector-${idx}`"
          data-lh-id="app-chat-schedule-turn-selection-hour-chip-selector"
          class="app-chat-schedule-turn-selection__hour-chip-selector"
          size="sm"
          :is-selected="selectedHourIdx === idx"
          @click="() => handleHourSelect(idx)">
          <template #content>
            <p class="pointer-events-none text-14 whitespace-nowrap">
              {{ hour.label_turn_hour }}
            </p>
          </template>
        </RoomieChipSelector>
      </AppSlider>
    </section>

    <RoomieButton
      id="app-chat-schedule-turn-selection-schedule-button"
      data-lh-id="app-chat-schedule-turn-selection-schedule-button"
      type="button"
      size="sm"
      variant="primary"
      :disabled="isScheduleCtaDisabled"
      @click="handleScheduleButton">
      <p class="text-14 pointer-events-none">
        {{ t('scheduleCta') }}
      </p>
    </RoomieButton>
  </section>
</template>

<i18n src="./i18n.json" lang="json" />
